// ABOUTME: Edits WhatsApp pre-registration activation, phone, model, and consent.
// ABOUTME: Keeps runtime activation explicit and preserves unavailable references.
import React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Paper,
    TextField,
    Typography
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { navigate } from "@gatsbyjs/reach-router";

import { WhatsappPreRegistrationLabels } from "./whatsappPreRegistrationLabels";
import {
    WhatsappPreRegistrationConfig,
    WhatsappPreRegistrationGeneralNumberEntryMode,
    WhatsappPreRegistrationOptions,
    getWhatsappPreRegistrationAvailableFlowModelVersion
} from "./whatsappPreRegistrationSettingsModel";

interface WhatsappPreRegistrationConfigBasicsProps {
    config: WhatsappPreRegistrationConfig;
    activeConfig: WhatsappPreRegistrationConfig;
    options: WhatsappPreRegistrationOptions;
    labels: WhatsappPreRegistrationLabels;
    createFlowModelLabel: string;
    isDirty: boolean;
    onChange: (config: WhatsappPreRegistrationConfig) => void;
}

const WhatsappPreRegistrationFlowModelVersionAlert = ({
    activeConfig,
    options,
    labels
}: {
    activeConfig: WhatsappPreRegistrationConfig;
    options: WhatsappPreRegistrationOptions;
    labels: WhatsappPreRegistrationLabels;
}) => {
    const availableFlowModelVersion =
        getWhatsappPreRegistrationAvailableFlowModelVersion({
            config: activeConfig,
            options
        });

    if (!availableFlowModelVersion) {
        return null;
    }

    return (
        <Box mt={2}>
            <Alert severity="warning">
                <Typography variant="body2">
                    {labels.formatFlowModelVersionAvailable({
                        activeVersion: activeConfig.flowModelVersion,
                        publishedVersion: availableFlowModelVersion.version
                    })}
                </Typography>
                <Typography variant="caption">
                    {labels.flowModelVersionAvailableHelp}
                </Typography>
            </Alert>
        </Box>
    );
};

const WhatsappPreRegistrationConfigBasics = ({
    config,
    activeConfig,
    options,
    labels,
    createFlowModelLabel,
    isDirty,
    onChange
}: WhatsappPreRegistrationConfigBasicsProps) => (
    <Paper elevation={0} style={{ padding: 24, height: "100%" }}>
        <Typography variant="h6">{labels.integration}</Typography>
        <Box mt={2}>
            <Alert severity={config.enabled ? "success" : "info"}>
                {config.enabled
                    ? labels.configurationActive
                    : labels.configurationInactive}
            </Alert>
        </Box>
        <Box mt={2}>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={config.enabled}
                        onChange={(event) =>
                            onChange({
                                ...config,
                                enabled: event.target.checked
                            })
                        }
                        color="primary"
                    />
                }
                label={labels.runtimeEnabled}
            />
            <Typography variant="caption" color="textSecondary">
                {labels.runtimeEnabledHelp}
            </Typography>
        </Box>
        <Box mt={2}>
            <TextField
                select
                fullWidth
                variant="outlined"
                label={labels.generalNumberEntryMode}
                value={config.generalNumberEntryMode}
                onChange={(event) =>
                    onChange({
                        ...config,
                        generalNumberEntryMode: event.target.value as
                            WhatsappPreRegistrationGeneralNumberEntryMode
                    })
                }
            >
                <MenuItem value="DISABLED">
                    {labels.generalNumberEntryDisabled}
                </MenuItem>
                <MenuItem value="UNMATCHED_ONLY">
                    {labels.generalNumberEntryUnmatchedOnly}
                </MenuItem>
                <MenuItem value="ALL_IDLE_CONTACTS">
                    {labels.generalNumberEntryAllIdleContacts}
                </MenuItem>
            </TextField>
            <Typography variant="caption" color="textSecondary">
                {labels.generalNumberEntryModeHelp}
            </Typography>
        </Box>
        <Box mt={3}>
            <TextField
                select
                fullWidth
                variant="outlined"
                label={labels.phoneNumber}
                value={config.phoneNumberId}
                onChange={(event) => {
                    const phoneNumberId = String(event.target.value || "");
                    const phone = options.phoneNumbers.find(
                        (option) => option.id === phoneNumberId
                    );
                    onChange({
                        ...config,
                        phoneNumberId,
                        displayPhoneNumber: phone?.displayPhoneNumber || ""
                    });
                }}
            >
                <MenuItem value="">-</MenuItem>
                {options.phoneNumbers.map((phone) => (
                    <MenuItem key={phone.id} value={phone.id}>
                        {phone.displayPhoneNumber}
                        {phone.available === false
                            ? ` (${labels.unavailable})`
                            : ""}
                    </MenuItem>
                ))}
            </TextField>
        </Box>
        <Box mt={2}>
            <TextField
                select
                fullWidth
                variant="outlined"
                label={labels.flowModel}
                value={
                    config.flowModelId
                        ? `${config.flowModelId}:${config.flowModelVersion}`
                        : ""
                }
                onChange={(event) => {
                    const key = String(event.target.value || "");
                    const model = options.flowModels.find(
                        (option) => `${option.id}:${option.version}` === key
                    );
                    onChange({
                        ...config,
                        flowModelId: model?.id || "",
                        flowModelVersion: model?.version || 0
                    });
                }}
            >
                <MenuItem value="">-</MenuItem>
                {options.flowModels.map((model) => (
                    <MenuItem
                        key={`${model.id}:${model.version}`}
                        value={`${model.id}:${model.version}`}
                    >
                        {`${model.name} · v${model.version}`}
                        {model.available === false
                            ? ` (${labels.unavailable})`
                            : ""}
                    </MenuItem>
                ))}
            </TextField>
            <Box mt={1} display="flex" justifyContent="flex-end">
                <Button
                    color="primary"
                    size="small"
                    disabled={isDirty}
                    onClick={() =>
                        navigate(
                            "/whatsappapplicationflowmodel/new?scope=PRE_REGISTRATION"
                        )
                    }
                >
                    {createFlowModelLabel}
                </Button>
            </Box>
            <WhatsappPreRegistrationFlowModelVersionAlert
                activeConfig={activeConfig}
                options={options}
                labels={labels}
            />
        </Box>
        <Box mt={2}>
            <TextField
                fullWidth
                variant="outlined"
                label={labels.consentVersion}
                value={config.consent.version}
                onChange={(event) =>
                    onChange({
                        ...config,
                        consent: {
                            ...config.consent,
                            version: event.target.value
                        }
                    })
                }
            />
        </Box>
        <Box mt={2}>
            <TextField
                fullWidth
                multiline
                minRows={3}
                variant="outlined"
                label={labels.consentText}
                value={config.consent.text}
                onChange={(event) =>
                    onChange({
                        ...config,
                        consent: {
                            ...config.consent,
                            text: event.target.value
                        }
                    })
                }
            />
        </Box>
    </Paper>
);

export default WhatsappPreRegistrationConfigBasics;
